import { ng } from "@common/ngds";
import { Drawer, Point, PathBuilder } from "@common/components/Rendering";
import {
  ArrowId,
  max_force_in_m,
  reaction_force_style,
  reaction_moment_style,
  reaction_force_text,
} from "../ViewportStyles";
import { GraphDirection } from "./SideView";
import { DiagramResults } from "./drawSectionForceDiagrams";
import { loadcases_getColor } from "../../helpers/Colors";
import { getSupports } from "../../helpers/supports";


export interface ReactionForce {
  hor: number;
  ver: number;
  hor_force: number;
  ver_force: number;
  moment: number;
  is_visible: boolean;
  color: string;
  baseSupport?: boolean;
}

export const collectReactionForces = (
  props: {
    graphDirection: GraphDirection;
    center_line_hor: number;
    project: ng.frontend.column.Project;
    is_visible: boolean;
    results: DiagramResults;
    selectedLcombGuid: string;
    reactionForces: ReactionForce[];
  },
) => {
  const supports = getSupports(props.project, props.graphDirection === "xy" ? "Y" : "Z");

  for (const sup of supports) {
    const xCoord = -sup.x;
    // add reaction force
    const forces = props.results.supportForces[props.selectedLcombGuid]?.[sup.support.guid] ?? [];
    const load_case_color = loadcases_getColor(props.project, props.selectedLcombGuid).toHex();
    const hor = props.center_line_hor;
    const ver = xCoord;
    const clean = (x: number) => {
      if (isFinite(x)) return x;
      else {
        return 0;
      }
    };
    const isBaseSupport = sup.isBaseSupport;

    for (const f of forces) {
      props.reactionForces.push({
        is_visible: props.is_visible,
        hor,
        ver,
        hor_force: clean(props.graphDirection === "xy" ? f.Ry.max : f.Rz.max),
        ver_force: clean(f.Rx.max),
        moment: clean(props.graphDirection === "xy" ? f.Mz.max : f.My.max),
        color: load_case_color,
        baseSupport: isBaseSupport,
      });
    }
  }
};

export const drawReactionForces = (g: Drawer<ArrowId>, reactionForces: ReactionForce[]) => {
  const min_force = 1e-9; //< min force possible when getting max force to avoid division by zero
  const min_moment = 1e-9; //< min moment possible when getting max force to avoid division by zero
  const hor_max = Math.max(...reactionForces.map((x) => Math.abs(x.hor_force)), min_force);
  const ver_max = Math.max(...reactionForces.map((x) => Math.abs(x.ver_force)), min_force);
  const mom_max = Math.max(...reactionForces.map((x) => Math.abs(x.moment)), min_moment);
  // console.log("max", max);

  // applies a minimum a amplitude when the force is zero so that the arrow direction is valid
  const force_min_amp = (f: number) => (f < 0 ? -1 : 1) * Math.max(Math.abs(f), 0.001);

  // determines if a force should be drawn (example: forces with a amplitude of 0)
  const is_force_visible = (f: number): boolean => Math.round(Math.abs(f / 10)) > 0; //round for 2 decimals

  const force_display_string = (f: number): string => (f / 1000).toFixed(2);

  for (const f of reactionForces) {
    if (is_force_visible(f.hor_force)) {
      // console.log("force", f.hor_force / max);
      const outer: Point = [f.hor - force_min_amp((f.hor_force / hor_max) * max_force_in_m), f.ver];
      g.drawLine(outer, [f.hor, f.ver], reaction_force_style, {
        display: f.is_visible,
        endArrow: "reaction-force-arrow",
      });
      g.drawText(outer, force_display_string(Math.abs(f.hor_force)), reaction_force_text, {
        horiAlign: "left",
        horiOffset: 6,
        display: f.is_visible,
      });
    }
    // todo(Gustav): should we draw vertical force?
    if (is_force_visible(f.ver_force)) {
      // console.log("force", f.ver_force / max);
      const outer: Point = [f.hor, f.ver + force_min_amp((f.ver_force / ver_max) * max_force_in_m)];
      g.drawLine(outer, [f.hor, f.ver], reaction_force_style, {
        display: f.is_visible,
        endArrow: "reaction-force-arrow",
      });
      g.drawText(outer, force_display_string(Math.abs(f.ver_force)), reaction_force_text, {
        horiAlign: "left",
        horiOffset: 6,
        display: f.is_visible,
      });
    }

    if (is_force_visible(f.moment)) {
      const outer: Point = [
        f.hor,
        f.ver + (f.baseSupport ? 0.2 : -0.2)
      ];

      const radius = 0.2;
      const sign = f.baseSupport ? 1 : -1;

      const arc_path = new PathBuilder();
      {
        let first = true;
        for (let i = 0; i <= 10; i++) {

          const x = sign * radius * Math.cos((i / 10) * 2 * Math.PI / 2) + outer[0];
          const y = sign * radius * Math.sin((i / 10) * 2 * Math.PI / 2) + outer[1];
          const p: Point = [x, y];

          if (first) arc_path.moveTo(p);
          else arc_path.drawLineTo(p);
          first = false;
        }
      }

      g.drawPath(reaction_moment_style, arc_path, {
        startArrow: f.moment > 0 ? "reaction-force-arrow" : undefined,
        endArrow: f.moment < 0 ? "reaction-force-arrow" : undefined,
        display: f.is_visible,
      });

      g.drawText(outer, force_display_string(Math.abs(f.moment)), reaction_force_text, {
        horiAlign: "left",
        horiOffset: 22,
        display: f.is_visible,
      });
    }

  }
};
